<template>
  <div class="tw-relative" data-testid="user-panel-access-picker">
    <SfSearchInput
      :model-value="query"
      :placeholder="placeholder"
      :aria-label="placeholder"
      :show-result-navigation="false"
      :focused-at="1"
      wide
      autocomplete="off"
      @update:model-value="runSearch"
      @clear="runSearch('')"
    />

    <div
      class="
        tw-absolute
        tw-left-0
        tw-right-0
        tw-top-full
        tw-z-10
        tw-mt-1
        tw-max-h-60
        tw-overflow-y-auto
        tw-rounded
        tw-border
        tw-border-solid
        tw-border-sf-slate-lighter-1
        tw-bg-sf-slate-white
        tw-shadow-lg
      "
      aria-live="polite"
    >
      <p
        v-if="searching"
        class="tw-m-0 tw-p-4 tw-text-sm tw-text-sf-slate-light-2"
      >
        {{ I18n.t('common.loading') }}
      </p>
      <p v-else-if="failed" class="tw-m-0 tw-p-4 tw-text-sm tw-text-sf-danger">
        {{ I18n.t('user.panel.search_error') }}
      </p>
      <p
        v-else-if="!visibleOptions.length"
        class="tw-m-0 tw-p-4 tw-text-sm tw-text-sf-slate-light-2"
      >
        {{ I18n.t('user.panel.no_search_results') }}
      </p>
      <!-- `SfList` keeps the browser's list padding. -->
      <SfList v-else no-margin class="tw-p-0">
        <SfListItem
          v-for="option in visibleOptions"
          :key="option.id"
          :item-id="String(option.id)"
          variant="lg"
          @item-click="emit('select', option)"
        >
          {{ option.name }}
        </SfListItem>
      </SfList>
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends AutocompleteOption">
import type { AutocompleteOption } from '@app/application/user-management/types'

import type { Ref } from 'vue'

import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

import {
  SEARCH_LIMIT,
  SEARCH_MAX_LIMIT,
} from '@app/application/user-management/constants'
import useI18n from '@app/shared/vue/composables/use-I18n'
import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import { SfListItem } from '@silverfin/ui-components'
import SfSearchInput from '@app/shared/vue/components/sf-search/search-input.vue'

defineOptions({ name: 'AccessPicker' })

const props =
  defineProps<{
    placeholder: string
    addedIds: number[]
    search: (
      query: string,
      signal: AbortSignal,
      limit: number
    ) => Promise<T[] | null>
  }>()

const emit = defineEmits<{ select: [option: T] }>()

const I18n = useI18n()

const query = ref('')
const options = ref<T[]>([]) as Ref<T[]>
const searching = ref(false)
const failed = ref(false)

// The server cuts the results to the limit before the added rows drop out, so
// the picker asks for one extra row per added row.
const searchLimit = computed(() =>
  Math.min(SEARCH_LIMIT + props.addedIds.length, SEARCH_MAX_LIMIT)
)

const visibleOptions = computed(() =>
  options.value
    .filter((option) => !props.addedIds.includes(option.id))
    .slice(0, SEARCH_LIMIT)
)

// One controller per picker: the panel can show two pickers at once, and a
// shared key would cancel the other one's search.
let controller: AbortController | null = null

onMounted(() => runSearch(''))
onBeforeUnmount(() => controller?.abort())

async function runSearch(value: string | null): Promise<void> {
  controller?.abort()
  controller = new AbortController()

  const { signal } = controller

  query.value = value ?? ''
  failed.value = false
  searching.value = true

  try {
    const results = await props.search(query.value, signal, searchLimit.value)

    if (signal.aborted) return

    options.value = results ?? []
  } catch {
    if (signal.aborted) return

    options.value = []
    failed.value = true
  }

  searching.value = false
}
</script>
